import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { useTrackAddedClipsToBoard, useTrackMovedClipsToBoard } from '@air/analytics';
import { type TaskData } from '@air/api/types';
import { THREE_SECONDS } from '@air/utilities';

import { AssetBoardMembershipUpdateTaskCompletedPanelItem } from '~/components/AssetBoardMembershipUpdateTasks/AssetBoardMembershipUpdateTaskCompletedPanelItem';
import { AssetBoardMembershipUpdateTaskFailedPanelItem } from '~/components/AssetBoardMembershipUpdateTasks/AssetBoardMembershipUpdateTaskFailedPanelItem';
import { AssetBoardMembershipUpdateTaskInProgressPanelItem } from '~/components/AssetBoardMembershipUpdateTasks/AssetBoardMembershipUpdateTaskInProgressPanelItem';
import { getMoveCacheRemovals } from '~/components/AssetBoardMembershipUpdateTasks/cacheUpdates';
import { PaneContainer } from '~/components/FileStatusTrackingPane/PaneContainer';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useReloadApplication } from '~/providers/ReloadApplicationProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeAssetBoardMembershipUpdateTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { type AssetBoardMembershipUpdateTask } from '~/store/tasks/types';
import { ReadyState } from '~/types/sockets';
import { useUpdateBoardStats } from '~/utils/mutateUtils/BoardStats';
import { useRefreshAssetsInAllViews, useRemoveAssetsFromAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { useClearBoardThumbnailsInAllViews, useRefreshBoardsInAllViews } from '~/utils/mutateUtils/GalleryBoards';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export type AssetBoardMembershipUpdateTaskItemProps = {
  onClear: () => void;
  task: AssetBoardMembershipUpdateTask;
};

const AssetBoardMembershipUpdateTaskItem = memo(({ task, onClear }: AssetBoardMembershipUpdateTaskItemProps) => {
  const operation = task.metadata.operation;

  if (task.status === 'completed') {
    const updatedCount = task.metadata.updatedAssetsCount ?? task.metadata.totalSelectedItemsCount;

    return (
      <AssetBoardMembershipUpdateTaskCompletedPanelItem
        onClear={onClear}
        operation={operation}
        updatedCount={updatedCount}
      />
    );
  }

  if (task.status === 'error') {
    return <AssetBoardMembershipUpdateTaskFailedPanelItem onClear={onClear} operation={operation} />;
  }

  return <AssetBoardMembershipUpdateTaskInProgressPanelItem onClear={onClear} operation={operation} />;
});

AssetBoardMembershipUpdateTaskItem.displayName = 'AssetBoardMembershipUpdateTaskItem';

export const PrivateAssetBoardMembershipUpdateTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeAssetBoardMembershipUpdateTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const tasks = useAirSelector(tasksSelector);
  const dispatch = useDispatch();
  const { cancelTask } = useCancelTask();
  const { onAddPendingAction, onRemovePendingAction } = useReloadApplication();
  const { refreshAssetsInAllViews } = useRefreshAssetsInAllViews();
  const { removeAssetsFromAllViews } = useRemoveAssetsFromAllViews();
  const { refreshBoardsInAllViews } = useRefreshBoardsInAllViews();
  const { clearBoardThumbnailsInAllViews } = useClearBoardThumbnailsInAllViews();
  const { updateBoardStats } = useUpdateBoardStats();
  const { trackAddedClipsToBoard } = useTrackAddedClipsToBoard();
  const { trackMovedClipsToBoard } = useTrackMovedClipsToBoard();
  const delayedRefreshTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'AssetBoardMembershipUpdate',
    remoteType: 'AssetBoardMembershipUpdater',
    onComplete: async ({ task, localTask, enrichMetadata }) => {
      const taskData: TaskData<'AssetBoardMembershipUpdater'>['data'] | undefined = task.data;
      const { boardId, includeDescendants, operation, trackLocation, totalSelectedItemsCount } = localTask.metadata;
      const targetBoardIds =
        operation === 'add' ? localTask.metadata.targetBoards.map(({ id }) => id) : [localTask.metadata.targetBoard.id];
      const removedMemberships = taskData?.removedMemberships ?? [];
      let movedSourceBoardIds: string[] = [];

      if (operation === 'move' && removedMemberships.length > 0) {
        const cacheRemovals = getMoveCacheRemovals({
          includeDescendants,
          removedMemberships,
          sourceBoardId: boardId,
          targetBoard: localTask.metadata.targetBoard,
        });
        movedSourceBoardIds = Array.from(new Set(cacheRemovals.map(({ boardId }) => boardId)));

        await Promise.all(
          cacheRemovals.map(({ assetIds, boardId: removalBoardId, boardQueryScope }) =>
            removeAssetsFromAllViews({
              assetIds,
              boardQueryScope,
              parentBoardId: removalBoardId,
            }),
          ),
        );
        clearBoardThumbnailsInAllViews(movedSourceBoardIds);
      } else if (operation === 'move') {
        // Compatibility fallback for tasks completed before removed memberships were returned.
        await refreshAssetsInAllViews({ parentBoardId: boardId });
      }

      const statsBoardIds = Array.from(
        new Set([
          ...targetBoardIds,
          ...(operation === 'move' ? [boardId, ...removedMemberships.map(({ boardId }) => boardId)] : []),
        ]),
      );

      await Promise.all([
        ...targetBoardIds.map((targetBoardId) => refreshAssetsInAllViews({ parentBoardId: targetBoardId })),
        ...statsBoardIds.map((statsBoardId) => updateBoardStats(statsBoardId)),
        refreshBoardsInAllViews('*'),
      ]);
      clearBoardThumbnailsInAllViews(movedSourceBoardIds);

      const delayedRefreshTimer = setTimeout(async () => {
        delayedRefreshTimersRef.current.delete(delayedRefreshTimer);
        await Promise.all([
          refreshBoardsInAllViews('*'),
          ...targetBoardIds.map((targetBoardId) => refreshAssetsInAllViews({ parentBoardId: targetBoardId })),
        ]);
      }, THREE_SECONDS);
      delayedRefreshTimersRef.current.add(delayedRefreshTimer);

      const { operation: completedOperation, ...completedMetadata } = taskData ?? {};
      enrichMetadata({
        ...completedMetadata,
        ...(completedOperation === 'add' || completedOperation === 'move' ? { operation: completedOperation } : {}),
      });

      if (!trackLocation) {
        return;
      }

      const updatedAssetsCount = taskData?.updatedAssetsCount ?? totalSelectedItemsCount;

      if (operation === 'add') {
        trackAddedClipsToBoard({
          boardIds: localTask.metadata.targetBoards.map(({ id }) => id),
          clipIds: [],
          location: trackLocation,
          selectAll: true,
          sourceBoardId: boardId,
          totalSelectionCount: updatedAssetsCount,
        });
      } else {
        trackMovedClipsToBoard({
          fromId: boardId,
          toId: localTask.metadata.targetBoard.id,
          clipIds: [],
          location: trackLocation,
          selectAll: true,
          totalSelectionCount: updatedAssetsCount,
        });
      }
    },
  });

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [currentWorkspace?.id, loadFromStorage]);

  const syncExecutedForReadyStateRef = useRef<boolean>();
  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  useEffect(() => {
    if (!isEmpty(tasks)) {
      onAddPendingAction({ id: 'asset-board-membership-update', description: 'Updating board assets' });
    } else {
      onRemovePendingAction('asset-board-membership-update');
    }
  }, [onAddPendingAction, onRemovePendingAction, tasks]);

  useUnmount(() => {
    delayedRefreshTimersRef.current.forEach(clearTimeout);
    delayedRefreshTimersRef.current.clear();
    dispatch(removeTasksAction({ taskIds: tasks.map(({ localTaskId }) => localTaskId) }));
  });

  if (tasks.length < 1) {
    return null;
  }

  return (
    <div data-testid="ASSET_BOARD_MEMBERSHIP_UPDATE_TASKS_PANEL">
      {tasks.map((task) => (
        <PaneContainer key={task.localTaskId} className="mb-2 last:mb-0">
          {/* biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration */}
          <AssetBoardMembershipUpdateTaskItem task={task} onClear={() => cancelTask(task.localTaskId)} />
        </PaneContainer>
      ))}
    </div>
  );
});

PrivateAssetBoardMembershipUpdateTasksPanel.displayName = 'PrivateAssetBoardMembershipUpdateTasksPanel';
